import React from 'react';

/**
 * Category / sub-category filter chips, shared by the Quizzes and Results pages.
 *
 * groups:   [{ key, label, count, subs: [{ name, count }] }]
 * total:    count shown on the "All" chip
 * category: selected group key ('' = all)
 * subcategory: selected sub-category name ('' = all within the category)
 * onChange(categoryKey, subcategory)
 *
 * When only one category is visible -- the usual case for a category manager --
 * a row holding a single chip is noise, so that category is treated as chosen
 * and the sub-category chips are shown straight away.
 */
const CategoryChips = ({ groups, total, category, subcategory, onChange }) => {
  if (!groups || groups.length === 0) return null;

  const single = groups.length === 1;
  const activeKey = category || (single ? groups[0].key : '');
  const active = groups.find(g => g.key === activeKey);
  const subs = active ? active.subs : [];

  // One category and nothing to split it by: there is nothing to filter.
  if (single && subs.length === 0) return null;

  return (
    <div className="quiz-filter">
      {!single && (
        <div className="filter-row" role="tablist" aria-label="Filter by category">
          <button
            type="button"
            className={`filter-chip ${!category ? 'active' : ''}`}
            onClick={() => onChange('', '')}
          >
            All <span className="chip-count">{total}</span>
          </button>
          {groups.map(g => (
            <button
              key={g.key}
              type="button"
              className={`filter-chip ${category === g.key ? 'active' : ''}`}
              onClick={() => onChange(g.key, '')}
            >
              {g.label} <span className="chip-count">{g.count}</span>
            </button>
          ))}
        </div>
      )}

      {subs.length > 0 && (
        <div className={`filter-row ${single ? '' : 'filter-subrow'}`} role="tablist" aria-label="Filter by sub-category">
          <span className="filter-label">{active.label}</span>
          <button
            type="button"
            className={`filter-chip small ${!subcategory ? 'active' : ''}`}
            onClick={() => onChange(category, '')}
          >
            All <span className="chip-count">{active.count}</span>
          </button>
          {subs.map(sub => (
            <button
              key={sub.name}
              type="button"
              className={`filter-chip small ${subcategory === sub.name ? 'active' : ''}`}
              onClick={() => onChange(activeKey, sub.name)}
            >
              {sub.name} <span className="chip-count">{sub.count}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Build chip groups from rows that each carry a category and optional
 * sub-category. `weight` lets a row stand for several items (a count).
 */
export const buildGroups = (rows, { keyOf, labelOf, subOf, weight = () => 1 }) => {
  const map = new Map();
  rows.forEach(r => {
    const key = keyOf(r);
    if (!map.has(key)) map.set(key, { key, label: labelOf(r), count: 0, subMap: new Map() });
    const g = map.get(key);
    const w = weight(r);
    g.count += w;
    const sub = subOf(r);
    if (sub) g.subMap.set(sub, (g.subMap.get(sub) || 0) + w);
  });
  return [...map.values()]
    .map(g => ({
      key: g.key,
      label: g.label,
      count: g.count,
      subs: [...g.subMap.entries()].sort((a, b) => a[0].localeCompare(b[0]))
        .map(([name, count]) => ({ name, count }))
    }))
    // Uncategorised last; everything else alphabetical.
    .sort((a, b) => (a.label === 'Uncategorised') - (b.label === 'Uncategorised')
      || a.label.localeCompare(b.label));
};

export default CategoryChips;
